import { ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { useDate } from '@/composables/useDate'
import { useDataSettingStore as useGlobalDataSettingStore } from '@/stores/dataSetting.stores'
import { useSystemVersionStore } from '@/stores/systemVersion.stores'
import dayjs from 'dayjs'

export const useDataSettingStore = defineStore('dashboardAccountInquiryDataSetting', () => {
  const { today, getFirstDayOfThisWeek, getEndDayOfThisWeek } = useDate()
  const { isNewSystem, isNewApi } = storeToRefs(useSystemVersionStore())

  // 本週結束日（週六）可能落在未來，只有新系統打 V2 API 才不接受超過今天的 endDate，需封頂
  // 在今天；舊系統（V1）沒有這個限制，維持完整的週日~週六
  const getDefaultEndDate = () => {
    const endOfWeek = getEndDayOfThisWeek()
    if (!isNewSystem.value || !isNewApi.value) return endOfWeek
    return dayjs(endOfWeek).isAfter(dayjs(today()), 'day') ? today() : endOfWeek
  }

  const startDate = ref(getFirstDayOfThisWeek())
  const endDate = ref(getDefaultEndDate())

  const globalDataSettingStore = useGlobalDataSettingStore()

  // 是否已套用日期篩選：由篩選視窗按下確認時明確標記，進頁/更新鈕的 reset() 收回。
  //
  // OP#2940：原本是從日期本身推斷——watcher 用「開始與結束同一天」當成尚未套用，
  // hasAppliedDateFilter 用「是否等於本週預設」判斷。加入小時篩選後「同一天的某幾個小時」
  // 變成合法查詢，第一個推斷會把日期換回本週、只留下使用者選的小時；第二個推斷則讓使用者
  // 手動選到剛好等於本週的區間時被當成沒篩選過。改用明確旗標，不再從日期回推意圖
  const hasAppliedDateFilter = ref(false)

  /** 篩選視窗按下確認時套用使用者選的區間 */
  const applyDateFilter = (newStartDate: string, newEndDate: string) => {
    hasAppliedDateFilter.value = true
    if (dayjs(newStartDate).isValid()) startDate.value = newStartDate
    if (dayjs(newEndDate).isValid()) endDate.value = newEndDate
  }

  // 尚未套用篩選時，全域日期的任何變動（進頁與更新鈕都會把它重設為 today()）都代表回到
  // 預設狀態，本頁維持本週。已套用後的區間由 applyDateFilter 直接寫入，不走這裡
  watch(
    () => [globalDataSettingStore.startDate, globalDataSettingStore.endDate],
    () => {
      if (hasAppliedDateFilter.value) return
      startDate.value = getFirstDayOfThisWeek()
      endDate.value = getDefaultEndDate()
    }
  )

  // endDate 的預設值取決於系統版本（V2 要封頂在今天、V1 維持完整週六），存成 ref 後
  // Sidebar 切換版本不會重算：舊值會讓下方 hasAppliedDateFilter（比對的是新版本的預設值）
  // 誤判成「已套用篩選」，舊→新時還可能把未來日期送給不接受未來日期的 V2
  watch([isNewSystem, isNewApi], () => {
    startDate.value = getFirstDayOfThisWeek()
    endDate.value = getDefaultEndDate()
  })

  // 是否已取得該店的結算時間設定（GetStoreTimeZone）：取得前查詢應保持停用，避免拿寫死的
  // 00:00 當預設時間送出真實查詢，取得後（不論成功失敗）才解除
  const isTimeSettingsReady = ref(false)
  const setTimeSettingsReady = () => {
    isTimeSettingsReady.value = true
  }

  // 搜尋條件不做快取：Pinia store 活在整個 app 生命週期，每次進頁都要重置回本週預設，
  // 並把 isTimeSettingsReady 收回 false，否則第二次進頁時查詢會在該店的 GetStoreTimeZone
  // 回來前就用上一家店殘留的時間送出
  const reset = () => {
    startDate.value = getFirstDayOfThisWeek()
    endDate.value = getDefaultEndDate()
    hasAppliedDateFilter.value = false
    isTimeSettingsReady.value = false
  }

  return {
    startDate,
    endDate,
    hasAppliedDateFilter,
    applyDateFilter,
    isTimeSettingsReady,
    setTimeSettingsReady,
    reset
  }
})
